import angular from 'angular';

import config from '../../../config';

export default function ManageUserCtrl(
  Authentication,
  Restangular,
  RestUtils,
  Users,
  PersonaCollection,
  Utils,
  UserUtils,
  Groups,
  $uibModalInstance,
  $window,
  $state,
  $scope,
  $q,
  focus,
  geo,
  previousState,
  user
) {
  'ngInject';

  focus('focus');

  const controller = this;
  controller.MANAGERS_ASSIGNED_TO_USERS_ENABLED = config.MANAGERS_ASSIGNED_TO_USERS_ENABLED;
  controller.isModal = Boolean($uibModalInstance);
  controller.user = user;
  controller.error = false;
  controller.groups = PersonaCollection.groups;
  controller.positions = PersonaCollection.positions;
  controller.timezones = RestUtils.getDefaultTimezones();
  controller.utils = UserUtils;
  controller.invitingPossible = Authentication.user.status !== 'unverified';
  controller.previousState = previousState;
  controller.inviteClicked = false;

  const addedUsers = [];

  Object.defineProperty(controller, 'locations', {
    get() {
      return Authentication.user.isAdmin
        ? PersonaCollection.locations
        : PersonaCollection.me.locations;
    },
  });

  controller.init = function init() {
    controller.systemRoles = [
      { role: 'manager', displayname: 'Manager' },
      { role: 'user', displayname: 'Employee' },
    ];

    if (Authentication.user.isAdmin) {
      controller.systemRoles.unshift({ role: 'admin', displayname: 'Admin' });
    }

    controller.rolesDescriptions = {
      user: 'Employees can communicate with coworkers and manage their shift schedules and tasks.',
      manager:
        'Managers can communicate with employees, create shift schedules and manage shift exchange requests and tasks within their locations.',
      admin:
        'Admins have full access to manage the organization’s settings, employees and locations; they can create shift schedules and manage shift exchange requests and tasks in all locations.',
    };

    if (!controller.user.timezone) {
      if (Authentication.user.timezone) {
        controller.user.timezone = Authentication.user.timezone;
      } else if (geo && geo.timezone) {
        controller.user.timezone = geo.timezone;
      }
    }
  };

  controller.init();

  function _failure(error) {
    if (error && error.status !== 502) {
      controller.error = Utils.getErrorMessages(error);
    }
  }

  function _createAnyGroups() {
    const newPositions = controller.user.positions.filter((p) => p.$$new);
    const newLocations = controller.user.locations.filter((l) => l.$$new);
    const newGroups = controller.user.groups.filter((g) => g.$$new);

    const promises = newPositions
      .concat(newLocations)
      .concat(newGroups)
      .map(async (group) => {
        try {
          const data = await Groups.withHttpConfig({ ignoreGlobalErrorHandler: true }).post(group);
          delete group.$$new;
          angular.extend(group, data);
          return data;
        } catch (error) {
          _failure(error);
        }
      });

    return $q.all(promises);
  }

  controller.availableGroups = UserUtils.getAvailableGroups(controller.user);

  controller.refreshManagers = () => {
    if (!controller.MANAGERS_ASSIGNED_TO_USERS_ENABLED) {
      return;
    }

    const currentUserManagerIds = controller.user.managers?.map((m) => m.id) || [];
    controller.availableManagers = UserUtils.getPossibleManagersForUser(controller.user).filter(
      (m) => !currentUserManagerIds.includes(m.id)
    );
  };

  controller.refreshManagers();

  controller.submit = async function submit(userParam, addAnother) {
    controller.error = false;

    await _createAnyGroups();
    userParam.full_phone = userParam.phone ? `${userParam.countryCode}${userParam.phone}` : null;

    if (!controller.invitingPossible) {
      userParam.invite = false;
    }

    let promise;
    if ('id' in userParam) {
      promise = userParam
        .withHttpConfig({ ignoreGlobalErrorHandler: true })
        .put({ 'user-fields': 'full' });
    } else {
      promise = Users.withHttpConfig({ ignoreGlobalErrorHandler: true }).post(userParam, {
        'user-fields': 'full',
      });
    }

    try {
      const data = await promise;
      addedUsers.push(data);
      if (addAnother) {
        controller.user = {
          countryCode: controller.user.countryCode,
          countryISOCode: controller.user.countryISOCode,
          timezone: controller.user.timezone,
          role: controller.user.role,
          locations: UserUtils.getLocationsForNewUser(
            PersonaCollection.me,
            PersonaCollection.locations
          ),
          positions: [],
          groups: [],
        };
        $scope.form.$setPristine();
        $scope.form.$setUntouched();
        controller.availableGroups = UserUtils.getAvailableGroups(controller.user);
        focus('focus');
      } else if ($uibModalInstance) {
        $uibModalInstance.close();
      } else {
        _goBack();
      }
    } catch (error) {
      _failure(error);
      throw error;
    }
  };

  function _goBack() {
    const defaultRoute = 'dashboard.manage.users.list';
    if (controller.previousState) {
      const targetRoute = controller.previousState.name || defaultRoute;
      const params = controller.previousState.params || {};

      $state.go(targetRoute, params);
      return;
    }

    $state.go(defaultRoute);
  }

  controller.cancel = function cancel() {
    _goBack();
  };

  controller.isInviteManuallyDisabled = false;
  controller.changedEmail = function changedEmail(email, form) {
    if (!controller.isInviteManuallyDisabled && email && form.$valid) {
      controller.user.invite = true;
      return;
    }

    if (!form.email.$viewValue) {
      controller.user.invite = false;
    }
  };

  controller.changedPhone = function changedPhone(phone, form) {
    if (!controller.isInviteManuallyDisabled && phone && form.$valid) {
      controller.user.invite = true;
      return;
    }
    if (!phone && !controller._emailForm?.email?.$viewValue) {
      controller.user.invite = false;
    }
  };

  controller.onInviteChange = function onInviteChange(value) {
    if (!value) {
      controller.isInviteManuallyDisabled = true;
    } else {
      controller.isInviteManuallyDisabled = false;
    }
  };

  controller.loadTimezones = async function loadTimezones() {
    controller.timezones = await RestUtils.getTimezones();
  };

  controller.canSendInvite = function canSendInvite() {
    return (
      (controller.user.phone && !controller._phoneForm.$invalid) ||
      (controller.user.email && !controller._emailForm.$invalid)
    );
  };

  controller.isPhoneRequired = function isPhoneRequired() {
    return controller.user.invite && controller.user.phone && !controller._phoneForm.$invalid;
  };

  controller.isEmailRequired = function isEmailRequired() {
    if (controller.user.invite) {
      if (controller.user.phone && !controller._phoneForm.$invalid) {
        return false;
      }

      return true;
    }

    return false;
  };

  controller.getLaborCostAccessTooltip = function getLaborCostAccessTooltip(role) {
    if (role === 'user') {
      return 'Only managers can be given access to labor cost information';
    }

    if (role === 'admin') {
      return 'All admins have access to labor cost information';
    }
  };

  controller.selectCountryCode = function selectCountryCode({ value, code }) {
    controller.user.countryCode = value;
    controller.user.countryISOCode = code;
  };

  $scope.$watchCollection('ctrl.user.locations', controller.refreshManagers);

  $scope.$watch('ctrl._emailForm.email.$viewValue', () => {
    if (!controller._emailForm?.email?.$viewValue && !controller.user.phone) {
      controller.user.invite = false;
    }
  });

  controller.removeManager = function removeManager(manager) {
    controller.user.managers = controller.user.managers?.filter((m) => m.id !== manager.id);
    controller.refreshManagers();
  };

  controller.addManager = function addManager(manager) {
    if (!controller.user.managers) {
      controller.user.managers = [];
    }

    controller.user.managers.push(manager);
    controller.refreshManagers();
  };
}
